import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { offers, studies as studiesApi } from '@/api/endpoints';
import type { Offer, StudyType } from '@/types';
import { Icon } from './Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { validateUploadSize, type UploadProgress } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

/** Domyślny czas realizacji zwykłego zlecenia (dni od daty zlecenia). */
const LEAD_TIME_DAYS = 30;

/** Dzisiejsza data w formacie YYYY-MM-DD (lokalny czas, bez przesunięcia strefowego). */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Dodaje `days` dni do daty YYYY-MM-DD i zwraca YYYY-MM-DD (parsowanie lokalne — bez off-by-one). */
function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

interface Props {
  open: boolean;
  offer: Offer;
  onClose: () => void;
  onStarted: (studyId: number) => void;
}

/**
 * Modal ścieżki B akceptacji oferty: klient przysłał własne zlecenie/zamówienie
 * (zamiast podpisywać naszą umowę). Geolog wgrywa plik (PDF/skan), wybiera typ
 * opracowania (pre-fill z line items oferty) i opcjonalny deadline. Backend
 * tworzy Study(contract=null) i załącza plik jako StudyAttachment CLIENT_ORDER.
 */
export function OfferStartWithoutContractModal({ open, offer, onClose, onStarted }: Props) {
  const [studyTypes, setStudyTypes] = useState<StudyType[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [studyTypeCode, setStudyTypeCode] = useState<string>('');
  const [title, setTitle] = useState('');
  const [orderDate, setOrderDate] = useState<string>('');
  const [deadline, setDeadline] = useState<string>('');
  // Dopóki geolog ręcznie nie zmieni terminu, trzymamy go zsynchronizowany z datą zlecenia (+30 dni).
  const [deadlineAuto, setDeadlineAuto] = useState(true);
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null); // null = brak wysyłki

  // Lista unikalnych studyType z line items oferty — pierwsza propozycja w selektorze.
  const suggestedStudyTypes = useMemo(() => {
    const codes = new Set<string>();
    for (const li of offer.lineItems ?? []) if (li.studyType) codes.add(li.studyType);
    return Array.from(codes);
  }, [offer]);

  useLayoutEffect(() => {
    if (!open) return;
    resetDirty();
    setErr(null); setBusy(false);
    setFile(null);
    setTitle(offer.title ?? '');
    const today = todayIso();
    setOrderDate(today);
    setDeadline(addDaysIso(today, LEAD_TIME_DAYS));
    setDeadlineAuto(true);
    setAttachmentDescription(`Zlecenie od klienta — przyjęte na podstawie oferty ${offer.number}`);
    studiesApi.types().then(ts => {
      setStudyTypes(ts);
      // Pre-fill: pierwszy ze studyType z line items, fallback do first available
      const defaultType = suggestedStudyTypes[0] ?? ts[0]?.id ?? '';
      setStudyTypeCode(defaultType);
      acceptChanges({ studyTypeCode: defaultType });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offer.id]);

  const { requestClose, confirmation, dialogProps, resetDirty, acceptChanges } = useUnsavedChanges({
    value: { file, studyTypeCode, title, orderDate, deadline, attachmentDescription }, onClose, open, busy,
  });

  if (!open) return null;

  const onOrderDateChange = (v: string) => {
    setOrderDate(v);
    // Termin nadąża za datą zlecenia (+30 dni), dopóki nie został ręcznie nadpisany.
    if (deadlineAuto && v) setDeadline(addDaysIso(v, LEAD_TIME_DAYS));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!studyTypeCode) { setErr('Wybierz typ opracowania'); return; }
    if (!offer.intakeId) { setErr('Oferta nie ma przypisanego ujęcia'); return; }
    // Walidacja rozmiaru (limit 100 MB) — zanim ruszy wysyłka.
    const sizeErr = validateUploadSize(file);
    if (sizeErr) { setErr(sizeErr); return; }
    setBusy(true); setErr(null); setUploadPct(0);
    try {
      const res = await offers.startWithoutContract(offer.id, file, {
        intakeId: offer.intakeId,
        studyType: studyTypeCode,
        title: title.trim() || undefined,
        orderDate: orderDate || undefined,
        deadline: deadline || undefined,
        attachmentDescription: attachmentDescription.trim() || undefined,
      }, (p: UploadProgress) => setUploadPct(p.percent));
      onStarted(res.studyId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false); setUploadPct(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-lg shadow-xl w-full max-w-[680px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100">
          <h2 className="text-[15px] font-semibold text-ink-900 flex items-center gap-2">
            <Icon name="file-text" className="w-4 h-4 text-water-600"/>
            Rozpocznij jako zwykłe zlecenie (bez umowy)
          </h2>
          <div className="text-[11px] text-ink-500 mt-1">
            Ścieżka B: pomijamy generowanie naszej umowy — opracowanie rusza na podstawie zaakceptowanej
            oferty jako zwykłe zlecenie. Plik zlecenia od klienta jest opcjonalny (dołącz, jeśli klient
            przysłał własne zamówienie). Opracowanie nie będzie miało powiązanego rekordu Contract.
          </div>
        </div>

        <form onSubmit={submit} className="p-5 grid gap-3">
          <label className="field">
            <span className="field-label">Plik zlecenia klienta (PDF lub skan) — opcjonalnie</span>
            <input type="file" accept="application/pdf,image/*"
                   onChange={e => {
                     const picked = e.target.files?.[0] ?? null;
                     // Walidacja rozmiaru (limit 100 MB) — blokujemy zbyt duży plik.
                     const sizeErr = validateUploadSize(picked);
                     if (sizeErr) { setErr(sizeErr); e.target.value = ''; setFile(null); return; }
                     setFile(picked);
                   }} className="input"/>
            {file ? (
              <span className="text-[11px] text-ink-500 mt-1">
                {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
              </span>
            ) : (
              <span className="text-[10px] text-ink-400 mt-1">
                Zostaw puste dla zwykłego zlecenia bez dokumentu od klienta.
              </span>
            )}
          </label>

          <label className="field">
            <span className="field-label">Typ opracowania *</span>
            <SearchableSelect value={studyTypeCode} onChange={setStudyTypeCode}>
              {studyTypes.map(t => (
                <option key={t.id} value={t.id}>
                  {t.abbreviation}-{t.code} — {t.name}
                  {suggestedStudyTypes.includes(t.id) ? ' (z oferty)' : ''}
                </option>
              ))}
            </SearchableSelect>
            {suggestedStudyTypes.length > 0 && (
              <span className="text-[10px] text-ink-400 mt-1">
                ⓘ Pre-fill na podstawie pozycji oferty. Możesz zmienić jeśli klient zamawia inny zakres.
              </span>
            )}
          </label>

          <label className="field">
            <span className="field-label">Tytuł opracowania</span>
            <input value={title} onChange={e => setTitle(e.target.value)} className="input"
                   placeholder="Pozostaw puste — zostanie wygenerowany z typu i ujęcia"/>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Data zlecenia</span>
              <input type="date" value={orderDate} onChange={e => onOrderDateChange(e.target.value)} className="input"/>
            </label>
            <label className="field">
              <span className="field-label flex items-center justify-between">
                <span>Termin realizacji</span>
                {!deadlineAuto && orderDate && (
                  <button type="button"
                          onClick={() => { setDeadline(addDaysIso(orderDate, LEAD_TIME_DAYS)); setDeadlineAuto(true); }}
                          className="text-water-600 hover:text-water-700 text-[10px] font-semibold">
                    ↺ +{LEAD_TIME_DAYS} dni
                  </button>
                )}
              </span>
              <input type="date" value={deadline}
                     onChange={e => { setDeadline(e.target.value); setDeadlineAuto(false); }} className="input"/>
              <span className="text-[10px] text-ink-400 mt-1">
                {deadlineAuto
                  ? `Liczony automatycznie: data zlecenia + ${LEAD_TIME_DAYS} dni. Możesz nadpisać.`
                  : 'Termin ustawiony ręcznie.'}
              </span>
            </label>
          </div>

          {file && (
            <label className="field">
              <span className="field-label">Opis załącznika</span>
              <input value={attachmentDescription} onChange={e => setAttachmentDescription(e.target.value)}
                     className="input"/>
            </label>
          )}

          <div className="text-[12px] text-ink-600 p-3 bg-ink-50 rounded border border-ink-200">
            <strong>Klient:</strong> {offer.client?.name ?? '—'}<br/>
            <strong>Ujęcie:</strong> {offer.intakeName ?? <span className="text-red-600">brak (uzupełnij ofertę)</span>}<br/>
            <strong>Z oferty:</strong> {offer.number} — {offer.title}
          </div>

          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}

          {uploadPct !== null && <UploadProgressBar percent={uploadPct} label="Wysyłanie pliku…" />}

          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy || !offer.intakeId} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5"/>
              {busy ? 'Tworzę opracowanie…' : 'Rozpocznij opracowanie'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
